import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import useDialogs from '@/hooks/useDialogs';
import { useGoToEntity } from '@/modules/tryIt/hooks/useGoToEntity';
import { useMessageBubble } from '@/modules/tryIt/hooks/useMessageBubble';
import { getActionsSelector } from '@/redux/dialogs/selectors';
import { popModal } from '@/redux/modals/actions';
import { requestActionSelection, requestRequisiteSelection } from '@/redux/nodes/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { AuthorType } from '@/shared/types/chat';
import {
  AmendPayload,
  Correction,
  Message,
  NodeStack,
  TryItCollection,
} from '@/shared/types/tryIt';

import AgentMessage from './AgentMessage';
import { PromptMessage } from './PromptMessage';
import {
  GroupedResponse,
  groupConsecutiveImages,
  ImageResponse,
} from './ResponseMessages/ImageResponse';
import { ResponseMessages } from './ResponseMessages/ResponseMessages';
import { ResponsesWrapper } from './ResponsesWrapper/ResponsesWrapper';

const bubbleBase =
  'rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.05),0_0_8px_rgba(0,0,0,0.03),0_30px_30px_rgba(0,0,0,0.02)] px-3 py-2 w-fit break-words max-w-96 [&_p]:text-sm [&_p]:leading-5 [&_p]:mb-1 [&_p]:font-normal [&_p:last-child]:mb-0 [&_a]:underline [&_a]:text-informational [&_a:hover]:text-accent [&_ol]:my-0 [&_ol]:pl-5 [&_ol]:flex [&_ol]:flex-col [&_ol]:gap-2 [&_ol]:list-decimal [&_ul]:my-0 [&_ul]:pl-5 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2 [&_ul]:list-disc';

export interface MessageBubbleProps {
  message: Message;
  onOptionClick: (text: string) => void;
  onPostback: (text: string) => void;
  collection?: TryItCollection;
  nodes_stack?: NodeStack[];
  brainId: string;
  author_type?: string;
  author_id?: string;
  author_name?: string;
  isAudio?: boolean;
  amendPayload?: AmendPayload;
  correction?: Correction;
  hideEntities?: boolean;
  flat?: boolean;
  /** Redesigned turn row, used by both test mode and replay. The legacy modal leaves it false. */
  turnRow?: boolean;
  hideAuthor?: boolean;
}

export const MessageBubble = ({
  message,
  onOptionClick,
  onPostback,
  collection,
  nodes_stack,
  brainId,
  author_type,
  author_id,
  author_name,
  amendPayload,
  correction,
  hideEntities,
  flat = false,
  turnRow = false,
  hideAuthor = false,
}: MessageBubbleProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const { onGoToEntity } = useMessageBubble(brainId);
  const entityLink = useGoToEntity();
  const { dialogs } = useDialogs(brainId);
  const allActions = getActionsSelector(dialogs);
  const dispatch = useDispatch();

  const canGoToResponse = useCallback(
    (actionId?: string) =>
      !!actionId && !!allActions?.some((a: { action_id: string }) => a.action_id === actionId),
    [allActions]
  );

  const goToResponse = useCallback(
    (actionId: string) => {
      const selectedAction = allActions.find(
        (a: { action_id: string }) => a.action_id === actionId
      );
      if (!brainId || !selectedAction || !actionId) {
        return;
      }

      dispatch(popModal());
      // select an action inside a requisite or an action of a node
      if (selectedAction?.if_no_requisite) {
        dispatch(
          requestRequisiteSelection({
            node_id: selectedAction.node_id,
            requisite_idx: selectedAction.requisite_idx,
            if_no_requisite: selectedAction.if_no_requisite,
          })
        );
      } else if (selectedAction.requisite_idx == null) {
        dispatch(requestActionSelection({ node_id: selectedAction.node_id, action_id: actionId }));
      } else {
        dispatch(
          requestRequisiteSelection({
            node_id: selectedAction.node_id,
            requisite_idx: selectedAction.requisite_idx,
          })
        );
      }
      navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${selectedAction.dialog_id}`);
    },
    [allActions, brainId, dispatch, navigate, slug]
  );
  switch (message.type) {
    case 'campaign':
    case 'broadcast': {
      return (
        <>
          {message.responses?.map((response, i) => (
            <ResponseMessages
              key={response.action_id ?? `${response.type}-${i}`}
              response={response}
              onOptionClick={onOptionClick}
              onPostback={onPostback}
              collection={collection}
              onResponseClick={goToResponse}
              canGoToResponse={canGoToResponse}
              authorType={author_type as `${AuthorType}`}
              isFirst={i === 0}
              flat={flat}
              turnRow={turnRow}
            />
          ))}
        </>
      );
    }
    case 'user':
      return (
        <PromptMessage
          text={message.text}
          entities={hideEntities ? [] : message.entities}
          onGoToEntity={onGoToEntity}
          entityHref={turnRow ? entityLink?.to : undefined}
          t={t}
          isAudio={message.isAudio}
          attachments={message?.attachments}
          reminder={message.reminder}
          isFlowResponse={message.isFlowResponse}
          flat={flat}
          turnRow={turnRow}
        />
      );
    case 'agent':
      return (
        <AgentMessage
          text={message.text || message.body.text}
          author_type="agent"
          author_id={author_id}
          author_name={author_name}
          template={message?.template}
          body={message?.body}
          turnRow={turnRow}
          hideAuthor={hideAuthor}
        />
      );
    case 'brain': {
      // Each image is its own action, so the redesigned row cards them separately.
      const responses = message.responses ?? [];
      const grouped: GroupedResponse[] = turnRow
        ? responses.map((response, index) => ({ type: 'single', response, index }))
        : groupConsecutiveImages(responses);
      const isWorkflow = nodes_stack && nodes_stack.length > 0;
      const nodeId = nodes_stack?.length === 1 ? nodes_stack[0].node_id : undefined;
      // Scope the amend affordance to the last text response so a turn has
      // exactly one amend entry point. Manual reverse loop (not findLastIndex)
      // because browserslist allows Safari 16.0 and findLastIndex needs 16.4.
      let lastTextIdx = -1;
      for (let i = grouped.length - 1; i >= 0; i -= 1) {
        const group = grouped[i];
        if (group.type !== 'image-group' && group.response.type === 'text') {
          lastTextIdx = i;
          break;
        }
      }
      return (
        <ResponsesWrapper nodes_stack={nodes_stack} flat={flat} showJumps={!turnRow}>
          {grouped.map((group, i) => {
            if (group.type === 'image-group') {
              return (
                <ImageResponse
                  key={`image-group-${group.startIndex}`}
                  responses={group.responses}
                  authorType={author_type as AuthorType}
                  icon={<span className="inline-flex size-4 shrink-0" />}
                />
              );
            }
            const isLastText = i === lastTextIdx;
            return (
              <ResponseMessages
                // Ungrouped images share one `action_id`, so the id alone collides.
                key={`${group.response.action_id ?? group.response.type}-${i}`}
                response={group.response}
                onOptionClick={onOptionClick}
                onPostback={onPostback}
                collection={collection}
                onResponseClick={goToResponse}
                canGoToResponse={canGoToResponse}
                amendPayload={isLastText ? amendPayload : undefined}
                correction={isLastText ? correction : undefined}
                authorType={author_type as AuthorType}
                isWorkflow={isWorkflow}
                nodeId={nodeId}
                isFirst={i === 0}
                flat={flat}
                turnRow={turnRow}
              />
            );
          })}
        </ResponsesWrapper>
      );
    }
    case 'error': {
      return (
        <p className={`${bubbleBase} bg-destructive text-primary-foreground`}>{message.text}</p>
      );
    }

    default:
  }
};
